import * as React from 'react';

const isTypingContext = (el: Element | null): boolean => {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el instanceof HTMLElement && el.isContentEditable) return true;
  const role = el.getAttribute('role');
  return role === 'textbox' || role === 'searchbox' || role === 'combobox';
};

/**
 * Press "/" anywhere on the page to focus a search input.
 * Common shortcut on Google, GitHub, Jira, YouTube, etc.
 *
 * Bail out when:
 * - Ctrl/Meta/Alt is held (so dev-tool / IDE shortcuts like Cmd+/ still work).
 *   Shift is intentionally allowed — on some keyboard layouts the "/" character
 *   is produced via Shift+key.
 * - An IME composition is in progress (covers JP/CN/KR input).
 * - Focus is in another text-entry context — native inputs, contenteditable, or
 *   ARIA combobox/textbox/searchbox roles (the search bar itself uses these).
 *
 * Scroll to top only if the input isn't currently visible — covered by a
 * sticky banner (e.g. course-page enroll bar) or scrolled offscreen.
 *
 * The structural ref type accepts both React.RefObject<HTMLInputElement> (used
 * by the consumer search bar) and React.MutableRefObject<HTMLInputElement |
 * undefined> (used by the enterprise search bar's autosuggest wrapper).
 */
const useSearchBarShortcut = (inputRef: { readonly current: HTMLInputElement | null | undefined }) => {
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== '/') return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.isComposing || e.keyCode === 229) return;
      if (isTypingContext(document.activeElement)) return;
      e.preventDefault();
      const input = inputRef.current;
      if (!input) return;
      const r = input.getBoundingClientRect();
      if (!input.contains(document.elementFromPoint(r.left + r.width / 2, r.top + r.height / 2))) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      input.focus();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [inputRef]);
};

export default useSearchBarShortcut;
